import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import logo from "./assets/BiorLabs-white.svg";
import { lockDocumentScroll } from "./utils/scrollLock";
import { focusFirstElement, trapFocus } from "./utils/focusTrap";

const MENU_ITEMS = [
  {
    label: "Cards",
    description: "Physical debit and virtual prepaid card products.",
    type: "route",
    value: "/cards",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M2 10h20" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 15h4" />
      </svg>
    ),
  },
  {
    label: "Frequently Asked Questions",
    description: "Product answers, onboarding help, and common questions.",
    type: "route",
    value: "/faq",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.09 9a3 3 0 115.82 1c0 2-3 2-3 4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 17h.01" />
        <circle cx="12" cy="12" r="9" />
      </svg>
    ),
  },
  {
    label: "Terms of Service",
    description: "Legal terms, privacy details, and website policies.",
    type: "route",
    value: "/tos",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h8" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 11h8" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 15h5" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 3h6l4 4v10a4 4 0 01-4 4H8a4 4 0 01-4-4V7a4 4 0 014-4z" />
      </svg>
    ),
  },
  {
    label: "Whitepaper",
    description: "Read the full product, token, and ecosystem overview.",
    type: "route",
    value: "/whitepaper",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14 3h-4a4 4 0 00-4 4v10a4 4 0 004 4h4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M14 3l5 5" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M14 3v5h5" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 12h4" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h4" />
      </svg>
    ),
  },
];

export const SideBar = ({ show, onHide = () => {} }) => {
  const drawerRef = useRef(null);
  const lastFocusedElementRef = useRef(null);

  useEffect(() => {
    if (!show) {
      return undefined;
    }

    lastFocusedElementRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const unlockScroll = lockDocumentScroll();
    const focusFrame = window.requestAnimationFrame(() => {
      focusFirstElement(drawerRef.current);
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onHide();
        return;
      }

      trapFocus(drawerRef.current, event);
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener("keydown", handleKeyDown);
      unlockScroll();
      lastFocusedElementRef.current?.focus?.();
    };
  }, [onHide, show]);

  const handleExternal = () => {
    onHide();
  };

  return (
    <div
      className={`fixed inset-0 z-[200] sm:hidden transition-opacity duration-200 ${
        show ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
      style={{
        height: "100dvh",
        background: "rgba(var(--page-bg-rgb), 0.76)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
      onClick={onHide}
    >
      <aside
        ref={drawerRef}
        aria-label="Mobile site navigation"
        aria-modal="true"
        role="dialog"
        tabIndex="-1"
        className={`mobile-site-drawer overflow-auto fixed top-0 right-0 transition-transform duration-300 ease-out ${
          show ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mobile-site-drawer__panel">
          <div className="mobile-site-drawer__glow mobile-site-drawer__glow--top" />
          <div className="mobile-site-drawer__glow mobile-site-drawer__glow--mid" />

          <div className="mobile-site-drawer__header">
            <div className="mobile-site-drawer__brand">
              <span className="mobile-site-drawer__brand-mark">
                <img src={logo} alt="BiorLabs" className="mobile-site-drawer__brand-logo" />
              </span>
              <div>
                <p className="mobile-site-drawer__eyebrow">BiorVault</p>
                <p className="mobile-site-drawer__brand-copy">Mobile navigation</p>
              </div>
            </div>

            <button
              type="button"
              aria-label="Close menu"
              className="mobile-site-drawer__close"
              onClick={onHide}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>

          <div className="mobile-site-drawer__stack">
            {MENU_ITEMS.map((item) => {
              const content = (
                <>
                  <span className="mobile-site-drawer__item-icon">{item.icon}</span>
                  <span className="mobile-site-drawer__item-copy">
                    <span className="mobile-site-drawer__item-title">{item.label}</span>
                    <span className="mobile-site-drawer__item-description">{item.description}</span>
                  </span>
                  <span className="mobile-site-drawer__item-arrow" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
                    </svg>
                  </span>
                </>
              );

              if (item.type === "route") {
                return (
                  <Link
                    key={item.label}
                    to={item.value}
                    className="mobile-site-drawer__item"
                    onClick={onHide}
                  >
                    {content}
                  </Link>
                );
              }

              return (
                <a
                  key={item.label}
                  href={item.value}
                  target="_blank"
                  rel="noreferrer"
                  className="mobile-site-drawer__item"
                  onClick={handleExternal}
                >
                  {content}
                </a>
              );
            })}
          </div>

        </div>
      </aside>
    </div>
  );
};
